/**
 * Support Request List – enterprise redesign with priority stripes and monospace IDs
 */
import { cn } from '@respectly/ui';
import { Clock, Building2, User } from 'lucide-react';
import type { SupportRequest } from '@respectly/shared-types';
import { SupportStatusBadge } from './SupportStatusBadge';
import { SupportPriorityBadge } from './SupportPriorityBadge';
import { formatDistanceToNow } from 'date-fns';

interface SupportRequestListProps {
  requests: SupportRequest[];
  activeId?: string | null;
  onSelect: (id: string) => void;
  isLoading?: boolean;
  className?: string;
}

const PRIORITY_STRIPE: Record<string, string> = {
  urgent: 'border-l-red-500',
  high:   'border-l-amber-500',
  medium: 'border-l-blue-500',
  low:    'border-l-zinc-300 dark:border-l-zinc-700',
};

export function SupportRequestList({
  requests,
  activeId,
  onSelect,
  isLoading,
  className,
}: SupportRequestListProps) {
  if (isLoading) {
    return (
      <div className={cn(className)}>
        {Array.from({ length: 6 }).map((_, i) => (
          <RequestSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-16 px-4 text-center', className)}>
        <div className="h-10 w-10 rounded-xl bg-zinc-100 dark:bg-zinc-800 ring-1 ring-zinc-200 dark:ring-zinc-700 flex items-center justify-center mb-3">
          <Building2 className="h-5 w-5 text-zinc-400" />
        </div>
        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">No requests</p>
        <p className="text-xs text-zinc-400 dark:text-zinc-500 text-center leading-relaxed">
          Try adjusting your filters or check back later.
        </p>
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {requests.map((request) => (
        <RequestItem
          key={request.id}
          request={request}
          isActive={request.id === activeId}
          onClick={() => onSelect(request.id)}
        />
      ))}
    </div>
  );
}

// ── Request Item ───────────────────────────────────────────────────────────────

function RequestItem({
  request,
  isActive,
  onClick,
}: {
  request: SupportRequest;
  isActive: boolean;
  onClick: () => void;
}) {
  const timeAgo = formatDistanceToNow(new Date(request.updatedAt ?? request.createdAt), { addSuffix: true });

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left border-b border-zinc-100 dark:border-zinc-800 border-l-4 p-4 transition-all duration-100',
        PRIORITY_STRIPE[request.priority] ?? 'border-l-zinc-200 dark:border-l-zinc-700',
        isActive
          ? 'bg-blue-50 dark:bg-blue-950/40'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/40'
      )}
    >
      {/* Top row: ID + Priority */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="font-mono text-[10px] text-zinc-400 dark:text-zinc-500 tracking-wide">
          #{request.id.slice(0, 8).toUpperCase()}
        </span>
        <SupportPriorityBadge priority={request.priority} size="sm" />
      </div>

      {/* Subject */}
      <h4 className={cn(
        'text-sm font-semibold line-clamp-1 mb-1.5 leading-snug',
        isActive ? 'text-blue-800 dark:text-blue-200' : 'text-zinc-900 dark:text-zinc-50'
      )}>
        {request.subject}
      </h4>

      {/* Tenant */}
      {request.tenant && (
        <div className="flex items-center gap-1.5 mb-2">
          <Building2 className="h-3 w-3 text-zinc-400 shrink-0" />
          <span className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{request.tenant.name}</span>
        </div>
      )}

      {/* Description preview */}
      <p className="text-xs text-zinc-400 dark:text-zinc-500 line-clamp-2 leading-relaxed mb-3">
        {request.description}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2">
        <SupportStatusBadge status={request.status} size="sm" />
        <div className="flex items-center gap-3">
          {request.assignedTo && (
            <span className="flex items-center gap-1 text-[11px] text-zinc-400">
              <User className="h-3 w-3" />
              {request.assignedTo.firstName}
            </span>
          )}
          <span className="flex items-center gap-1 text-[11px] text-zinc-400">
            <Clock className="h-3 w-3" />
            {timeAgo}
          </span>
        </div>
      </div>
    </button>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function RequestSkeleton() {
  return (
    <div className="border-b border-zinc-100 dark:border-zinc-800 border-l-4 border-l-zinc-200 dark:border-l-zinc-700 p-4 animate-pulse">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="h-3 bg-zinc-100 dark:bg-zinc-800 rounded w-20" />
        <div className="h-4 bg-zinc-100 dark:bg-zinc-800 rounded-full w-12 shrink-0" />
      </div>
      <div className="h-4 bg-zinc-100 dark:bg-zinc-800 rounded w-3/4 mb-2" />
      <div className="h-3 bg-zinc-100 dark:bg-zinc-800 rounded w-1/3 mb-2" />
      <div className="space-y-1 mb-3">
        <div className="h-3 bg-zinc-100 dark:bg-zinc-800 rounded w-full" />
        <div className="h-3 bg-zinc-100 dark:bg-zinc-800 rounded w-2/3" />
      </div>
      <div className="flex items-center justify-between">
        <div className="h-4 bg-zinc-100 dark:bg-zinc-800 rounded-full w-16" />
        <div className="h-3 bg-zinc-100 dark:bg-zinc-800 rounded w-16" />
      </div>
    </div>
  );
}
